import React from 'react';
import { Helmet } from 'react-helmet-async';
import '../styles.css';

export default function PublishingPage() {
  return (
    <main className="publishing-page">
      <Helmet>
        <title>
          HermesReads Publishing | Revived Works, Translations, and New Editions
        </title>

        <meta
          name="description"
          content="HermesReads Publishing recovers neglected works, produces new translations, and publishes carefully edited digital and print editions with clear standards, transparent methods, and lasting availability."
        />

        <meta
          name="keywords"
          content="HermesReads Publishing, public domain publishing, translated books, revived books, classic literature editions, digital editions, print editions, neglected works, forgotten books, European literature"
        />

        <meta
          property="og:title"
          content="HermesReads Publishing | Revived Works, Translations, and New Editions"
        />

        <meta
          property="og:description"
          content="Neglected works, new translations, and carefully edited digital and print editions published for contemporary readers."
        />

        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://www.hermesreads.com/publishing"
        />

        <meta
          property="og:image"
          content="https://www.hermesreads.com/android-chrome-512x512.png"
        />

        <meta name="twitter:card" content="summary_large_image" />

        <meta
          name="twitter:title"
          content="HermesReads Publishing | Revived Works and Translations"
        />

        <meta
          name="twitter:description"
          content="Neglected works, new translations, and carefully produced digital and print editions."
        />

        <meta
          name="twitter:image"
          content="https://www.hermesreads.com/android-chrome-512x512.png"
        />

        <link
          rel="canonical"
          href="https://www.hermesreads.com/publishing"
        />

        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'HermesReads Publishing',
            url: 'https://www.hermesreads.com/publishing',
            parentOrganization: {
              '@type': 'Organization',
              name: 'HermesReads',
              url: 'https://www.hermesreads.com/',
            },
            description:
              'HermesReads Publishing recovers neglected works, produces new translations, and publishes carefully edited digital and print editions with transparent methods and lasting availability.',
          })}
        </script>
      </Helmet>

      <section className="publishing-hero">
        <p className="publishing-kicker">
          HermesReads Publishing
        </p>

        <h1>
          Revived works, new translations, and carefully produced editions.
        </h1>

        <p className="publishing-lead">
          HermesReads Publishing publishes selected public-domain works, new
          translations, and revived editions for contemporary readers. Through
          careful translation, editing, design, and publication, the aim is to
          bring valuable books back into circulation in high-quality digital
          and print editions.
        </p>
      </section>

      <section
        className="publishing-recruitment"
        aria-labelledby="publishing-recruitment-heading"
      >
        <div className="publishing-recruitment-content">
          <p className="publishing-recruitment-kicker">
            Join HermesReads
          </p>

          <h2 id="publishing-recruitment-heading">
            Interested in helping bring neglected books back into circulation?
          </h2>

          <p className="publishing-recruitment-intro">
            HermesReads is an ongoing publishing project and welcomes people
            with an interest in literature, languages, history, philosophy,
            religion, publishing, design, technology, and the humanities. As
            the catalogue grows, we are always interested in hearing from
            people who would like to contribute.
          </p>

          <div className="publishing-recruitment-grid">
            <article>
              <h3>Translation and languages</h3>

              <p>
                Translate older European works, compare texts with their
                originals, assist with terminology, revise existing
                translations, or contribute other forms of linguistic
                expertise.
              </p>
            </article>

            <article>
              <h3>Editing, research, and publishing</h3>

              <p>
                Help prepare books through editing, proofreading, historical
                research, introductions, references, design, metadata,
                software, production, or other work connected with
                publication.
              </p>
            </article>
          </div>

          <p className="publishing-recruitment-requirements">
            There is no single background we are looking for. Some contributors
            may be experienced translators, editors, researchers, or
            designers, while others may simply have strong language skills,
            subject knowledge, technical ability, editorial judgment, or a
            serious enthusiasm for books. If you believe you could contribute
            to the project, we would be pleased to hear from you.
          </p>

          <a
            href="mailto:noah@hermesreads.com?subject=Working%20with%20HermesReads"
            className="publishing-recruitment-button"
          >
            Work with HermesReads
          </a>
        </div>
      </section>

      <section className="publishing-statement">
        <h2>What HermesReads publishes</h2>

        <p
          style={{
            textAlign: 'center',
            maxWidth: '900px',
            margin: '0 auto 1.5rem',
          }}
        >
          HermesReads publishes works that remain intellectually valuable but
          have fallen out of ordinary circulation: untranslated books,
          neglected editions, difficult archival texts, and works poorly
          served by existing digital reproductions.
        </p>

        <p
          style={{
            textAlign: 'center',
            maxWidth: '900px',
            margin: '0 auto',
          }}
        >
          Publishing, preservation, and technical production are treated as
          parts of the same task. A text is not truly recovered until it is
          readable, accurately presented, properly described, and available to
          readers in durable digital and print editions. In some cases this
          work is carried out in cooperation with publishing and content
          partners.
        </p>
      </section>

      <section className="publishing-statement">
        <h2>Translation standards and editorial process</h2>

        <p
          style={{
            textAlign: 'center',
            maxWidth: '900px',
            margin: '0 auto 1.5rem',
          }}
        >
          HermesReads publishes both conventionally translated editions and
          AI-assisted translations. The choice of method depends on the
          individual work, the complexity of its language, the expertise
          available, and the editorial aims of the edition.
        </p>

        <p
          style={{
            textAlign: 'center',
            maxWidth: '900px',
            margin: '0 auto 1.5rem',
          }}
        >
          Conventional translations are produced and revised through ordinary
          human translation and editorial work. In AI-assisted editions,
          language models are used as tools within a broader editorial process,
          rather than as substitutes for editorial judgment or responsibility.
        </p>

        <p
          style={{
            textAlign: 'center',
            maxWidth: '900px',
            margin: '0 auto 1.5rem',
          }}
        >
          AI-assisted translations are revised, edited, checked against the
          source text, and reviewed before publication. Particular attention is
          given to meaning, terminology, tone, historical context, names,
          references, consistency, readability, and fidelity to the original
          work. Passages that are ambiguous, difficult, or dependent on
          specialist knowledge receive additional scrutiny.
        </p>

        <p
          style={{
            textAlign: 'center',
            maxWidth: '900px',
            margin: '0 auto',
          }}
        >
          Every translated edition clearly identifies the method used.
          Conventionally translated and AI-assisted editions are marked as
          such, allowing readers to understand how the work was produced.
          Whatever the method, HermesReads remains responsible for the
          editorial quality and presentation of the finished edition.
        </p>
      </section>

      <section
        className="publishing-grid"
        aria-label="Publishing areas"
      >
        <article className="publishing-card">
          <h2>Public-domain revivals</h2>

          <p>
            Many older works survive only as poor scans, incomplete
            transcriptions, badly formatted files, or obscure archival
            reproductions. HermesReads restores selected texts as readable,
            carefully edited, and professionally presented digital and print
            editions.
          </p>

          <ul>
            <li>Classic and forgotten works</li>
            <li>Recovered and corrected texts</li>
            <li>Readable digital editions</li>
            <li>Professionally prepared print editions</li>
          </ul>
        </article>

        <article className="publishing-card">
          <h2>Translations</h2>

          <p>
            Many important European works have never appeared in English, exist
            only in outdated translations, or remain inaccessible outside
            specialist circles. HermesReads publishes new English editions
            through both conventional translation and carefully reviewed
            AI-assisted methods, depending on the needs of the individual work.
          </p>

          <ul>
            <li>Conventionally translated editions</li>
            <li>Clearly disclosed AI-assisted editions</li>
            <li>Comparison with original-language texts</li>
            <li>Editorial revision and quality review</li>
          </ul>
        </article>

        <article className="publishing-card">
          <h2>Digital editions</h2>

          <p>
            Digital books should be edited and designed as books, not merely
            converted into files. HermesReads gives close attention to
            structure, navigation, typography, notes, references, and reliable
            presentation across modern reading devices.
          </p>

          <ul>
            <li>Structured EPUB editions</li>
            <li>Clear navigation and chapter hierarchy</li>
            <li>Consistent notes and references</li>
            <li>Reliable presentation across reading devices</li>
          </ul>
        </article>

        <article className="publishing-card">
          <h2>Print editions</h2>

          <p>
            Selected works are also published as professionally prepared
            physical editions, with the interior, cover, typography, pagination,
            metadata, and ISBNs coordinated as parts of a single publication.
          </p>

          <ul>
            <li>Carefully typeset interiors</li>
            <li>Coordinated cover and interior design</li>
            <li>ISBN and bibliographic organization</li>
            <li>Consistency across digital and print editions</li>
          </ul>
        </article>

        <article className="publishing-card">
          <h2>Series and catalogue building</h2>

          <p>
            HermesReads develops coherent author series, thematic collections,
            and bodies of related works. These editions are planned together so
            that their editorial standards, design, translation practices, and
            presentation remain consistent across the catalogue.
          </p>

          <ul>
            <li>Collected works and author series</li>
            <li>Thematic and historical collections</li>
            <li>Consistent editorial standards</li>
            <li>Shared design and presentation</li>
          </ul>
        </article>

        <article className="publishing-card">
          <h2>Neglected and difficult-to-place works</h2>

          <p>
            Some valuable works fall outside the categories favored by
            conventional publishing. They may be too specialized, too obscure,
            too old, or simply too demanding to prepare economically.
            HermesReads is built to work with precisely this kind of material.
          </p>

          <ul>
            <li>Historical essays</li>
            <li>Political and philosophical texts</li>
            <li>Religious and esoteric works</li>
            <li>Rare literary and cultural material</li>
          </ul>
        </article>
      </section>

      <section className="publishing-principles">
        <h2>Publishing principles</h2>

        <div className="publishing-principles-grid">
          <div>
            <span>01</span>

            <h3>Recover neglected works</h3>

            <p>
              A work should not disappear because its surviving editions are
              rare, badly scanned, poorly translated, badly formatted, or
              difficult to classify.
            </p>
          </div>

          <div>
            <span>02</span>

            <h3>Publish readable editions</h3>

            <p>
              Every edition should be clearly structured, carefully edited,
              professionally presented, and comfortable to read in both digital
              and print form.
            </p>
          </div>

          <div>
            <span>03</span>

            <h3>Preserve editorial transparency</h3>

            <p>
              Readers should know how an edition was produced. Translation
              methods, editorial interventions, and the use of AI assistance
              are disclosed clearly rather than hidden behind vague language.
            </p>
          </div>

          <div>
            <span>04</span>

            <h3>Build for lasting availability</h3>

            <p>
              Every edition is prepared to professional publishing standards,
              with careful attention to editing, typography, metadata,
              discoverability, bibliographic consistency, and compatibility
              across bookselling, library, and reading systems.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}